"use client";

import { forwardRef } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  SectionSeparator,
  type SeparatorPosition,
  type SeparatorVariant,
} from "@/components/ui/SectionSeparator";

interface SectionProps {
  id?: string;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  noContainer?: boolean;
  noPadding?: boolean;
  separator?: {
    position: SeparatorPosition;
    variant: SeparatorVariant;
    className?: string;
    fillClassName?: string;
  };
}

export const Section = forwardRef<HTMLElement, SectionProps>(
  (
    {
      id,
      title,
      subtitle,
      children,
      className,
      contentClassName,
      noContainer,
      noPadding,
      separator,
    },
    ref,
  ) => {
    return (
      <section
        ref={ref}
        id={id}
        className={cn(
          "relative w-full",
          !separator && "flex flex-col",
          className,
        )}
      >
        {/* Top Separator - Rendered visually at the top */}
        {separator?.position === "top" && (
          <SectionSeparator
            position="top"
            variant={separator.variant}
            className={separator.className}
            fillClassName={separator.fillClassName}
          />
        )}

        {/* Main Content with Padding */}
        <div className={cn("w-full flex-1", !noPadding && "py-24 md:py-32")}>
          <div
            className={cn(
              !noContainer && "container",
              "relative z-10",
              !noPadding && "px-6",
              contentClassName,
            )}
          >
            {(title || subtitle) && (
              <div className="mb-16 text-center">
                {subtitle && (
                  <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-(--brand-primary) font-medium text-sm tracking-wider uppercase mb-2 block"
                  >
                    {subtitle}
                  </motion.span>
                )}
                {title && (
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="text-3xl md:text-5xl font-bold font-outfit text-(--text-primary)"
                  >
                    {title}
                  </motion.h2>
                )}
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: 0.2,
                    duration: 0.8,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="h-1 w-24 bg-(--brand-primary) mx-auto mt-6"
                />
              </div>
            )}
            {children}
          </div>
        </div>

        {/* Bottom Separator - Rendered visually at the bottom */}
        {separator?.position === "bottom" && (
          <SectionSeparator
            position="bottom"
            variant={separator.variant}
            className={separator.className}
            fillClassName={separator.fillClassName}
          />
        )}
      </section>
    );
  },
);

Section.displayName = "Section";
